import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { UserDisplay } from "@components/common/user-display";
import { TeamCategoryChipIcon } from "@components/table/teams/team-category-chip-icon";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { Dialog, CloseIcon, Button } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useTeamUtils } from "@hooks/use-team-utils";
import { useToast } from "@hooks/use-toast";
import { Box, Typography } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { convertToTeamMemberTypes } from "@store/members/members-slice-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { deleteTeam } from "@store/teams/teams-slice-thunk";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { HIDE_ON_HOVER_CLASS, SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useState } from "react";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";

interface Props {
  /** The team data */
  team: FaroCoreApiTypes.Team;
}

/** Render team table action elements */
export function TeamsTableAction({ team }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { companyId } = useAppParams();
  const { trackEvent } = useTrackEvent();
  const { openTeamDetails } = useTeamUtils();
  const { showToast } = useToast();
  const { isRemovingTeam } = useAppSelector(fetchingTeamsFlagsSelector);
  const { canDeleteTeams } = useHasUserValidRoleCompanyLevel();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onViewDetailsClick(): Promise<void> {
    openTeamDetails({
      teamId: team.id,
      numberOfMembers: team.memberCount,
    });
  }

  /** Handles removing a team */
  async function removeTeam(): Promise<void> {
    trackEvent({
      name: TeamEvents.deleteTeam,
      props: { numberOfMembers: team.memberCount },
    });

    if (!companyId) {
      throw new Error("companyId is required");
    }

    try {
      await dispatch(deleteTeam({ companyId, teamId: team.id })).unwrap();

      showToast({
        type: "success",
        message: `${capitalizeFirstLetter(
          TEAM_DISPLAY_NAME
        )} deleted successfully`,
      });
    } finally {
      // Error is handled by error slice
    }
  }

  return (
    <Box>
      {teamDisplayProperties[team.category.kind].isAutoManaged && (
        <Box
          className={HIDE_ON_HOVER_CLASS}
          sx={{ display: "flex", justifyContent: "end" }}
        >
          <TeamCategoryChipIcon
            kind={team.category.kind}
            shouldShowText={true}
          />
        </Box>
      )}

      <Box
        className={SHOW_ON_HOVER_CLASS}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "right",
          visibility: "hidden",
          width: "100%",
          height: "0px",
        }}
      >
        {/* View details link */}
        {teamDisplayProperties[team.category.kind].isAutoManaged && (
          <TeamCategoryChipIcon kind={team.category.kind} />
        )}

        <Button
          variant="ghost"
          size="s"
          sx={{ textTransform: "inherit" }}
          onClick={onViewDetailsClick}
        >
          View details
        </Button>

        {/* Delete team button */}
        {canDeleteTeams && !team.category.isManaged && (
          <>
            <SphereActionDivider />
            <SphereTooltip title={`Remove ${TEAM_DISPLAY_NAME}`}>
              <SphereIconButton
                component={CloseIcon}
                onClick={() => setIsDialogOpen(true)}
              />
            </SphereTooltip>
          </>
        )}

        {/* Dialog before a team to be removed */}
        <Dialog
          title={`Remove ${TEAM_DISPLAY_NAME}?`}
          confirmText="Remove"
          open={isDialogOpen}
          onConfirm={removeTeam}
          onCancel={() => setIsDialogOpen(false)}
          showSpinner={isRemovingTeam}
        >
          <Typography
            sx={{
              fontSize: "14px",
              marginTop: "0px",
              marginBottom: SPACE_ELEMENTS_OF_MODAL,
            }}
          >
            This action will only remove the selected {TEAM_DISPLAY_NAME}. All
            the members of the {TEAM_DISPLAY_NAME} will remain in the workspace.
          </Typography>

          <UserDisplay member={convertToTeamMemberTypes(team)} />
        </Dialog>
      </Box>
    </Box>
  );
}
